// Customize tab — auto-detects colors from template HTML and lets users swap them.

import { useMemo, useState } from 'react'

interface CustomizeTabProps {
  htmlContent: string
  onHtmlChange: (html: string) => void
}

// Well-known color labels for common email template colors
const COLOR_LABELS: Record<string, string> = {
  '#f97316': 'Accent / CTA',
  '#111111': 'Dark (header/text)',
  '#f4f4f5': 'Background',
  '#ffffff': 'White',
  '#52525b': 'Body text',
  '#71717a': 'Secondary text',
  '#a1a1aa': 'Muted text',
  '#e4e4e7': 'Borders',
  '#d4d4d8': 'Light text',
  '#fafafa': 'Card background',
}

const LOGO_PATTERN = /https?:\/\/[^\s"']*placehold\.co[^\s"']*[Ll]ogo[^\s"']*/g

/** Extract unique hex colors from inline styles in the HTML. */
function detectColors(html: string): string[] {
  const matches = html.match(/#[0-9a-fA-F]{6}/g)
  if (!matches) return []
  const counts = new Map<string, number>()
  for (const m of matches) {
    const lower = m.toLowerCase()
    counts.set(lower, (counts.get(lower) ?? 0) + 1)
  }
  // Sort by frequency (most used first), filter out very rare (1 occurrence)
  return Array.from(counts.entries())
    .filter(([, count]) => count >= 2)
    .sort((a, b) => b[1] - a[1])
    .map(([color]) => color)
}

function labelFor(hex: string): string {
  return COLOR_LABELS[hex] ?? `Used in template`
}

export function CustomizeTab({ htmlContent, onHtmlChange }: CustomizeTabProps): React.JSX.Element {
  const [logoUrl, setLogoUrl] = useState('')
  const [logoError, setLogoError] = useState(false)

  const detectedColors = useMemo(() => detectColors(htmlContent), [htmlContent])
  const [replacements, setReplacements] = useState<Record<string, string>>({})

  function handleApplyLogo(): void {
    if (!logoUrl.trim()) return
    onHtmlChange(htmlContent.replace(LOGO_PATTERN, logoUrl.trim()))
  }

  function handleApplyColors(): void {
    let updated = htmlContent
    // Sort replacements by length descending to avoid partial matches
    const entries = Object.entries(replacements).filter(([from, to]) => from !== to)
    for (const [from, to] of entries) {
      updated = updated.replaceAll(from, to)
    }
    onHtmlChange(updated)
    setReplacements({})
  }

  const hasChanges = Object.entries(replacements).some(([from, to]) => from !== to)
  const trimmedUrl = logoUrl.trim()
  const showPreview = trimmedUrl.length > 0 && !logoError

  return (
    <div className="space-y-6 rounded-xl border border-surface-3 bg-surface-0 p-6">
      {/* Logo section */}
      <section>
        <h3 className="text-sm font-medium text-text-primary">Logo</h3>
        <p className="mt-1 text-xs text-text-secondary">
          Replace placeholder logos with your brand logo.
        </p>
        <div className="mt-3 flex items-end gap-3">
          <input
            type="text"
            value={logoUrl}
            onChange={(e) => { setLogoUrl(e.target.value); setLogoError(false) }}
            placeholder="https://example.com/logo.png"
            className="flex-1 rounded-lg border border-surface-3 bg-surface-0 px-3 py-1.5 text-sm text-text-primary placeholder:text-text-tertiary focus:border-brand-500 focus:outline-none"
          />
          <button type="button" onClick={handleApplyLogo} disabled={!trimmedUrl} className="shrink-0 rounded-lg border border-surface-3 px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-surface-1 disabled:opacity-50">
            Apply Logo
          </button>
        </div>
        {showPreview && (
          <div className="mt-2 flex h-10 w-[120px] items-center justify-center overflow-hidden rounded border border-surface-3 bg-surface-1">
            <img src={trimmedUrl} alt="Logo preview" className="max-h-full max-w-full object-contain" onError={() => setLogoError(true)} />
          </div>
        )}
      </section>

      {/* Colors section — auto-detected */}
      <section>
        <h3 className="text-sm font-medium text-text-primary">Colors</h3>
        <p className="mt-1 text-xs text-text-secondary">
          {detectedColors.length} colors detected in your template. Pick new values and apply.
        </p>
        {detectedColors.length === 0 ? (
          <p className="mt-3 text-sm text-text-tertiary">No inline colors found in the HTML.</p>
        ) : (
          <>
            <div className="mt-3 space-y-2">
              {detectedColors.map((hex) => (
                <div key={hex} className="flex items-center gap-3 rounded-lg bg-surface-1 px-3 py-2">
                  <span className="inline-block h-6 w-6 shrink-0 rounded border border-surface-3" style={{ backgroundColor: hex }} />
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-xs text-text-primary">{hex}</p>
                    <p className="text-[10px] text-text-tertiary">{labelFor(hex)}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-text-tertiary">→</span>
                    <input
                      type="color"
                      value={replacements[hex] ?? hex}
                      onChange={(e) => setReplacements((p) => ({ ...p, [hex]: e.target.value }))}
                      className="h-7 w-7 shrink-0 cursor-pointer rounded border border-surface-3"
                    />
                    <input
                      type="text"
                      value={replacements[hex] ?? hex}
                      onChange={(e) => setReplacements((p) => ({ ...p, [hex]: e.target.value }))}
                      className="w-20 rounded border border-surface-3 bg-surface-0 px-2 py-1 font-mono text-xs text-text-primary focus:border-brand-500 focus:outline-none"
                    />
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={handleApplyColors}
              disabled={!hasChanges}
              className="mt-3 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
            >
              Apply Colors
            </button>
          </>
        )}
      </section>

      <p className="text-xs text-text-tertiary">
        Changes are applied as find-and-replace in the HTML. Switch to the Code tab to see the result.
      </p>
    </div>
  )
}
